/**
 * DOWNSPOUT (2026-10-04, Matt): "Every gutter needs at least one downspout." A placed
 * downspout is a single-node `path` anchor ON a gutter line (the outlet's station);
 * everything else — the drop from the gutter, the welded offset back to the wall below
 * the eave, the brackets, and where it ends (kick-out onto a splash block, into a
 * sub-drain at grade, or ~6" above a lower roof onto a splash pan) — is DERIVED from
 * the host roof's gutter, the wall under it and the ground or roof below
 * (bim-geometry categories/downspout). Shape, bends and ending default to the host
 * ROOF's settings ("Fascia & Gutters"); each can be overridden here. Size defaults to
 * the drainage sizing (the roof area the downspout carries). Every downspout is placed by
 * hand (2026-10-12 — the automatic ones a gutter run used to get are gone; older models
 * kept theirs as placed downspouts, converted once on open).
 */
import type { CategorySchema } from '../../registry'

export const downspoutSchema: CategorySchema = {
  id: 'downspout',
  label: 'Downspout',
  pickRank: 1,
  typeParams: {
    groups: [
      {
        label: 'Downspout',
        params: [
          { key: 'material', label: 'Material (blank = the gutter\'s)', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Downspout',
        params: [
          // single-node anchor ON the gutter (the outlet station along the run)
          { key: 'path', label: 'Outlet', kind: 'path2d', default: null },
          // PAINT BRUSH (2026-10-07): the brushed color (a colors.ts ref) — blank = its finish / role
          { key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true },
          {
            key: 'shape', label: 'Shape', kind: 'choice', default: 'roof',
            choices: [
              { value: 'roof', label: 'Roof default' },
              { value: 'round', label: 'Round' },
              { value: 'rect', label: 'Rectangular' },
            ],
          },
          {
            key: 'size', label: 'Size', kind: 'choice', default: 'auto',
            choices: [
              { value: 'auto', label: 'Sized from the roof area' },
              { value: '3', label: '3" round' },
              { value: '4', label: '4" round' },
              { value: '2x3', label: '2x3 rectangular' },
              { value: '3x4', label: '3x4 rectangular' },
            ],
          },
          {
            key: 'bend', label: 'Bends', kind: 'choice', default: 'roof',
            choices: [
              { value: 'roof', label: 'Roof default' },
              { value: 'radius', label: 'Welded, radiused' },
              { value: 'miter', label: 'Welded, mitered' },
            ],
          },
          {
            key: 'end', label: 'Ends', kind: 'choice', default: 'roof',
            choices: [
              { value: 'roof', label: 'Roof default' },
              { value: 'grade', label: 'Spill to grade (kick-out + splash block)' },
              { value: 'subdrain', label: 'Into sub-drain at grade' },
            ],
          },
        ],
      },
    ],
  },
}
